/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import { ScrollBarData } from './ScrollBarData';
import ClassUtil from '../../ClassUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Class for creating a desktop friendly scrollbar using a slider.
 */
@cc._decorator.ccclass
@cc._decorator.requireComponent(cc.ScrollView)
export default class ScrollBarComponent extends ComponentBase
{
    /** Slider handler method name. */
    public static readonly SliderHandlerName: string = 'onSliderProgressChanged';
    /** ScrollView handler method name. */
    public static readonly ScrollViewHandlerName: string = 'onScrollViewChanged';
    /** Prefab which contains [[ScrollBarData]]. */
    @property(cc.Prefab)
    protected sliderPrefab: cc.Prefab = null;
    /** Percentage of content height should be scaled to. */
    @property(cc.Float)
    protected heightPercentage: number = 1;
    /** Percentage increase or decrease in sensitivity of scrolling from mouse wheel. */
    @property(cc.Float)
    protected mouseWheelSensitivity: number = 1;
    /** Lowest value for handle height. */
    @property(cc.Float)
    protected minHandleSize: number = 10;
    /** ZIndex to set newly instantiate [[ScrollBarData]] component to. */
    @property(cc.Integer)
    protected containerZIndex: number = 1;
    /** Direction of this ScrollBar. */
    @property({ type: cc.Scrollbar.Direction })
    protected direction: cc.Scrollbar.Direction = cc.Scrollbar.Direction.VERTICAL;
    /** If true, will scale content size to accomadate ScrollBar width. */
    @property()
    protected scaleContentToFit: boolean = true;
    /** Margin left or top when scaling content. */
    @property(cc.Float)
    protected margin: number = 30;
    /** If false, will hide scrollbar when content height is less than view (mask). */
    @property(cc.Float)
    protected adjustment: number = 0;
    @property()
    protected alwaysShow: boolean = false;

    /** Created prefab handle. */
    protected scrollBarData: ScrollBarData = null;
    /** Hook component on this node.  */
    protected scrollView: cc.ScrollView = null;

    /** True if ScrollBar vertical direction. */
    private _isVertical: boolean = true;
    /** Scale ratio of content adjusted to fit. */
    private _ratio: number = 1;

    private _hasScaled: boolean = false;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();
        this._isVertical = this.direction === cc.Scrollbar.Direction.VERTICAL;

        // If mobile, disable and bail.
        if (cc.sys.isMobile || cc.sys.isNative || 
            (cc.sys.os === cc.sys.OS_OSX && (('ontouchstart' in window) || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0)))
        {
            this.enabled = false;
            return;
        }

        if (!this.sliderPrefab)
        {
            CLog.error('No slider prefab.', this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }

        // Fetch sibling component.
        this.scrollView = this.getComponent(cc.ScrollView);
        this.scrollView.cancelInnerEvents = false;

        this.scrollView.inertia = false;
        this.scrollView.elastic = false;
        this.scrollView.bounceDuration = 0;

        this.scrollView.vertical = false;

        if (!this.sliderPrefab)
        {
            CLog.error('No prefab set for desktop ScrollBarComponent.', this, LogTypes.Initialization);

            this.enabled = false;
            return;
        }

        this.scrollBarData = cc.instantiate(this.sliderPrefab).getComponent(ScrollBarData);

        if (!this.scrollBarData)
        {
            CLog.error(`Prefab does not contain ${ScrollBarData.name} component.`, this, LogTypes.Initialization);

            this.enabled = false;
            return;
        }

        // Remove lame ScrollBar
        if (this._isVertical)
        {
            let verticalScrollBar: cc.Scrollbar = this.scrollView.verticalScrollBar;
            if (verticalScrollBar) { verticalScrollBar.node.destroy(); }
        }
        else
        {
            let horizontalScrollBar: cc.Scrollbar = this.scrollView.horizontalScrollBar;
            if (horizontalScrollBar) { horizontalScrollBar.node.destroy(); }

            // Swap width height about if horizontal.
            let oldWidth: number = this.scrollBarData.node.width;
            this.scrollBarData.node.width = this.scrollBarData.node.height;
            this.scrollBarData.node.height = oldWidth;
        }

        this.scrollView.node.addChild(this.scrollBarData.node);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        // Bring to front.
        this.scrollBarData.node.zIndex = this.containerZIndex;

        // Only re-engage inertia on touch start.
        this.scrollView.node.on(cc.Node.EventType.TOUCH_START, this.onScrollViewTouchStartHandler.bind(this));
        this.scrollView.node.on(cc.Node.EventType.MOUSE_WHEEL, this.onScrollViewMouseWheelHandler.bind(this));

        // Add mousewheel events to scrollbar.
        this.scrollBarData.HandleNode.on(cc.Node.EventType.MOUSE_WHEEL, this.onMouseWheelHandler.bind(this));
        this.scrollBarData.HandleNode.parent.on(cc.Node.EventType.MOUSE_WHEEL, this.onMouseWheelHandler.bind(this));

        this.scrollBarData.Slider.node.active = false;
    }

    /** @hidden */
    protected firstUpdate(): void
    {
        this.updateAndReset();
        this.createHandlers();
    }

    private updateAndReset(): void {
        this.updateUI();
        this.resetPositions();
    }

    /** Creat handlers to tie into [[ScrollView]] and [[Slider]] built-in events. */
    private createHandlers(): void
    {
        // Create Slider Handler
        this.AddComponentEventHandler(
            ClassUtil.getName(ScrollBarComponent),
            ScrollBarComponent.SliderHandlerName,
            this.onSliderTouchMovedHandler.bind(this),
            this.scrollBarData.Slider.slideEvents
        );

        // Create Slider Handler
        this.AddComponentEventHandler(
            ClassUtil.getName(ScrollBarComponent),
            ScrollBarComponent.ScrollViewHandlerName,
            this.onScrollViewMovedHandler.bind(this),
            this.scrollView.scrollEvents
        );

        this.scrollView.content.on('child-added', this.updateAndReset.bind(this));
        this.scrollView.content.on('child-removed', this.updateAndReset.bind(this));
    }

    /** Handler for when movement occurs on the slider. */
    private onSliderTouchMovedHandler = (event: cc.Event.EventCustom = null) =>
    {
        this.scrollView.vertical = true;

        ComponentBase.CancelEventHandler(event);
        if (!this.scrollBarData.Slider.enabled) { return; }

        let scrollOffset: cc.Vec2 = this.scrollView.getScrollOffset().mul(this._ratio);
        let maxOffset: cc.Vec2 = this.scrollView.getMaxScrollOffset().mul(this._ratio);

        if (this._isVertical)
        {
            this.scrollView.scrollTo(new cc.Vec2(
                (scrollOffset.y / maxOffset.y) * this._ratio,
                1 - this.scrollBarData.Slider.progress * this._ratio
            ), 0);
        }
        else
        {
            this.scrollView.scrollTo(new cc.Vec2(
                this.scrollBarData.Slider.progress * this._ratio,
                (scrollOffset.x / maxOffset.x) * this._ratio
            ), 0);
        }

        this.scrollView.vertical = false;
    }

    /** Handler for mouse wheel on ScrollView. */
    private onScrollViewMouseWheelHandler = (event: cc.Event.EventMouse): void =>
    {
        if (event.target !== this.node) { return; }

        this.onMouseWheelHandler(event);

        event.bubbles = false;
        event.stopPropagationImmediate();
    }

    /** Handler for mouse wheel on ScrollBar. */
    private onMouseWheelHandler = (event: cc.Event.EventMouse) =>
    {
        this.scrollView.inertia = false;

        let scrollAmmount: number = this._isVertical ? event.getScrollY() : event.getScrollX();

        let newProgress: number
            = this.scrollBarData.Slider.progress - ((scrollAmmount / 10000) * this.mouseWheelSensitivity);
        this.moveTo(newProgress);

        this.onSliderTouchMovedHandler(null);
    }

    /**
     * Handler for when [[ScrollView]] movement occurs to
     * update position of [[cc.Slider]].
     */
    private onScrollViewMovedHandler = () =>
    {
        let scrollOffset: cc.Vec2 = this.scrollView.getScrollOffset().mul(this._ratio);
        let maxOffset: cc.Vec2 = this.scrollView.getMaxScrollOffset().mul(this._ratio * this.heightPercentage);

        if (this._isVertical)
        {
            this.moveTo(scrollOffset.y / maxOffset.y);
        }
        else
        {
            this.moveTo(scrollOffset.x / maxOffset.x);
        }
    }

    /**
     * Handler to re-engage inertia on [[cc.ScrollView]] touch start.
     */
    private onScrollViewTouchStartHandler(event: cc.Event.EventCustom): void
    {
        event.bubbles = false;
        event.stopPropagationImmediate();
    }

    /**
     * Set slider progress to constrained amount.
     * @param newProgress Progress to set [[cc.Slider]] to.
     */
    private moveTo(newProgress: number): void
    {
        if (!this.scrollBarData.Slider.enabled) { return; }
        if (newProgress < 0) { return; }

        if (newProgress === 0 || isNaN(newProgress))
        {
            newProgress = 0;
            if (this.scrollBarData.Slider.progress === newProgress) { return; }
        }

        if (newProgress > 1)
        {
            newProgress = 1;
            if (this.scrollBarData.Slider.progress === newProgress) { return; }
        }

        this.scrollBarData.Slider.progress = newProgress;
    }

    /**
     * Updat UI based on content size.
     */
    private updateUI(): void
    {
        const contentParent: cc.Size = this.scrollView.content.parent.getBoundingBox();

        const contentWidth: number = (this._isVertical ? contentParent.height : contentParent.height) * this._ratio;
        const scrollViewWidth: number = this._isVertical
            ? this.scrollView.content.height
            : this.scrollView.content.width;

        let newWidth: number =  contentWidth * (contentWidth / (scrollViewWidth || 1));

        // Maintain minimum.
        if (newWidth < this.minHandleSize)
        {
            newWidth = this.minHandleSize;
            this.setEnabledAndActive();
        }
        else if (newWidth >= contentWidth)
        {
            // Disable when no scrolling needed.
            newWidth = this.scrollBarData.ScrollBarBGNode.width;
            this.setEnabledAndActive(false, this.alwaysShow);
            return;
        }
        else
        {
            // In range.
            this.setEnabledAndActive();
            const maxWidth: number = contentWidth / 4;
            if (newWidth > maxWidth) { newWidth = maxWidth; }
        }

        this.updateLayout();
        this.updateScrollbarHeight(newWidth);

        // Update widget.
        this.updateWidgetComponent();
        this.onSliderTouchMovedHandler();
    }

    private updateLayout(): void
    {
        // If contained in a layout, update layout.
        let layout: cc.Layout = this.scrollView.content.getComponent(cc.Layout);
        if (layout) { layout.updateLayout(); }
    }

    /** Udated width of slider to be porportional to content height. */
    private updateScrollbarHeight(handleHeight: number): void
    {
        this.scrollBarData.HandleNode.width = handleHeight;

        // Setup height in relation to content size.
        let sliderLength: number = this._isVertical
            ? this.scrollView.content.parent.height
            : this.scrollView.content.parent.width;

        sliderLength *= this.heightPercentage;

        this.scrollBarData.Slider.node.width = sliderLength - handleHeight;
        this.scrollBarData.ScrollBarBGNode.width = sliderLength;

        this.scrollBarData.Slider.node.y = this.scrollView.content.parent.y;
    }

    /** Upated widget positioning for node. */
    private updateWidgetComponent(): void
    {
        // Ensure widget aligned correctly.
        let widget: cc.Widget = ComponentBase.GetOrAddComponent(this.scrollBarData.node, cc.Widget);
        widget.target = this.scrollBarData.node.parent;

        if (this._isVertical)
        {
            this.scrollBarData.Slider.node.rotation = 90;

            widget.isAlignTop = true;
            widget.top = this.adjustment;

            widget.isAlignRight = true;
            widget.right = this.margin;

            widget.isAlignBottom = true;
            widget.bottom = 0;

            widget.isAlignLeft = false;
        }
        else
        {
            this.scrollBarData.Slider.node.rotation = 0;

            widget.isAlignTop = false;

            widget.isAlignRight = true;
            widget.right = 0;

            widget.isAlignBottom = true;
            widget.bottom = this.margin;

            widget.isAlignLeft = true;
            widget.left = this.adjustment;
        }

        widget.isAlignOnce = false;
    }

    /**
     * Resets position off Slider and ScrollView.
     */
    private resetPositions(): void
    {
        this.scrollBarData.Slider.progress = 0;
        this._isVertical ? this.scrollView.scrollToTop() : this.scrollView.scrollToLeft();
    }

    /**
     * Sets interactive and visiblity.
     * @param enabled whether to aneable interaction.
     * @param active set container active state.
     */
    private setEnabledAndActive(enabled: boolean = true, active: boolean = true): void
    {
        if (active)
        {
            this.scaleLayout();
        }

        this.enabled = enabled;
        this.scrollBarData.Slider.enabled = enabled;
        this.scrollBarData.Slider.handle.node.active = enabled;

        this.scrollBarData.node.active = active;
        this.scrollBarData.Slider.node.active = active;

        if (!enabled && active) { this.scrollBarData.Slider.progress = 0; }
    }

    private scaleLayout(): void {
        if (!this.scaleContentToFit || this._hasScaled) { return; }

        const margin: number = this.margin + this.scrollBarData.HandleNode.height;

        // Scale content in to account for width of the scrollbar itself.
        if (this._isVertical)
        {
            let contentWidth: number = this.scrollView.content.width;
            this._ratio = (contentWidth - this.scrollBarData.node.width - margin) / contentWidth;
        }
        else
        {
            let contentHeight: number = this.scrollView.content.height;
            this._ratio = (contentHeight - this.scrollBarData.node.height - margin) / contentHeight;
        }

        if (this._ratio > 0) { this.scrollView.content.scale *= this._ratio; }

        this._hasScaled = true;
        this.updateLayout();
    }
}
